// FLAG:useDocuAi — keeps the artifact set of the preview switcher (original / XML / validation
// report / report / attachments) up to date after the document was opened.
//
// Why this exists: DocuAI's parent result and its children (extracted XML, KoSIT validation report,
// report, embedded attachments) reach the DMS as SEPARATE RabbitMQ events that are consumed
// concurrently, and the DMS publishes "analysis ready" as soon as the PARENT is persisted (see the
// `TODO(child-gate)` in dms-s3 docuai-child-ingest.service.ts). So a review opened right after the
// analysis regularly sees only some of the children — and every consumer here reads the file exactly
// once, which is why the missing entries only appeared after a page reload.
//
// This hook re-resolves ONLY the artifact fields, never the review data: the form is seeded from its
// own load and must never be re-seeded, or the reviewer loses edits in progress.
//
// It is deliberately not an open-ended poll. It refreshes when the analysis-finished event for this
// file arrives, plus a short bounded catch-up for the window where the children land after that
// event. It stops as soon as two consecutive resolves return the same artifact set — so a document
// without artifacts (a plain PDF receipt) costs exactly one extra request, not a poll loop.
import { useCallback, useEffect, useRef, useState } from 'react'
import FileService from '@/modules/file/fileService'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import { InvoicePreviewSource } from '@/modules/document/utils/invoice-preview-documents'
import { artifactSignature, ArtifactFields } from '@/modules/document/utils/artifact-signature'
import { ackUiEvent } from '@/modules/document/utils/ui-event-ack'

// Catch-up schedule, in ms after mount. Short enough to feel immediate, bounded so a document that
// never gains children cannot keep firing requests.
const REFRESH_DELAYS_MS = [2500, 6000, 12000]

interface UseInvoiceArtifactsResult {
  /** The most recently resolved file body, or null while the first resolve is still running. */
  resolved: InvoicePreviewSource | null
  /** True only while no body has been resolved yet. */
  isResolving: boolean
}

export const useInvoiceArtifacts = (fileId?: string | number | null): UseInvoiceArtifactsResult => {
  const [resolved, setResolved] = useState<InvoicePreviewSource | null>(null)
  const [isResolving, setIsResolving] = useState(!!fileId)
  // Mirrors `resolved` for the callbacks below without making them depend on it (a changing
  // dependency would restart the catch-up timers on every refresh).
  const signatureRef = useRef<string>('')
  const isMountedRef = useRef(true)
  const isSettledRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  const resolve = useCallback(async(isInitial: boolean, canSettle = true) => {
    if (!fileId) return
    try {
      const { body } = await FileService.fetchFileInfo(fileId)
      if (!isMountedRef.current) return
      const nextSignature = artifactSignature(body as ArtifactFields)
      // Nothing new — the artifact set has settled, stop spending requests on it. Only the
      // bounded catch-up schedule may draw that conclusion (canSettle): the event-path resolve
      // races the children by design, so an unchanged set there proves nothing yet.
      if (!isInitial && nextSignature === signatureRef.current) {
        if (canSettle) isSettledRef.current = true
        return
      }
      signatureRef.current = nextSignature
      setResolved(body as InvoicePreviewSource)
    } catch {
      // A failed refresh must never cost the user the artifacts already on screen.
      if (isInitial && isMountedRef.current) setResolved(null)
    } finally {
      if (isInitial && isMountedRef.current) setIsResolving(false)
    }
  }, [fileId])

  // One owner for the catch-up timers, so BOTH arming points (mount and the analysis-finished
  // event) share the same cleanup: re-arming REPLACES the pending schedule instead of stacking
  // a second one next to it.
  const catchUpTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([])
  const clearCatchUp = useCallback(() => {
    catchUpTimeoutsRef.current.forEach(clearTimeout)
    catchUpTimeoutsRef.current = []
  }, [])
  const startCatchUp = useCallback(() => {
    clearCatchUp()
    catchUpTimeoutsRef.current = REFRESH_DELAYS_MS.map(delay => setTimeout(() => {
      if (!isSettledRef.current) resolve(false)
    }, delay))
  }, [clearCatchUp, resolve])

  useEffect(() => {
    signatureRef.current = ''
    isSettledRef.current = false
    setResolved(null)
    setIsResolving(!!fileId)
    if (!fileId) return undefined

    resolve(true)
    startCatchUp()

    return () => { clearCatchUp() }
  }, [fileId, resolve, startCatchUp, clearCatchUp])

  // The analysis-finished event is the moment children start landing; re-resolve immediately
  // instead of waiting for the next catch-up tick, and RE-ARM the schedule — a re-analysis
  // ("Erneut analysieren") finishes long after the mount timers have expired, so without a
  // fresh schedule this single resolve would run exactly in the window where the children are
  // not linked yet, see an unchanged set, and nothing would ever refresh again. For the same
  // reason the event resolve itself must not settle (canSettle=false): settling is earned by
  // two identical resolves of the re-armed catch-up, not by racing the children once.
  const onFileAnalyzed = useCallback((message) => {
    if (`${message?.data?.id}` !== `${fileId}`) return
    if (message?.data?.analysisInProgress === true) return
    isSettledRef.current = false
    resolve(false, false)
    startCatchUp()
    // FLAG:useDocuAi — this hook is one of the FILE:ANALYSED consumers, so it acks the terminal it
    // just processed (see document/utils/ui-event-ack: every consumer must ack, or the delivery row
    // stays outstanding and is re-broadcast until the attempt cap). Scoped naturally: the fileId
    // guard above means we only ever ack the event for OUR document, never another file's.
    ackUiEvent(message?.data?.eventId)
  }, [fileId, resolve, startCatchUp])
  useWebSocketEvent([EventTypes.FILE_ANALYZED], onFileAnalyzed)

  return { resolved, isResolving }
}

export default useInvoiceArtifacts
